import { useState, useRef, useEffect, type ReactNode } from 'react';
import {
  Pencil,
  Check,
  X,
  Loader2,
  FileText,
  Calculator,
  Database,
  User as UserIcon,
} from 'lucide-react';

interface Props {
  /** Libellé du champ (au-dessus). */
  label: string;
  /** Valeur courante (affichage). */
  value: string | number | null | undefined;
  /** Type input HTML. */
  type?: 'text' | 'email' | 'tel' | 'number' | 'date';
  /** Si fourni, rend un select avec ces options. */
  options?: { value: string; label: string }[];
  /** Texte affiché en complément. */
  extra?: string;
  /** Mise en valeur visuelle (badge coloré). */
  highlight?: 'primary' | 'violet';
  /** Si fourni, le champ est ÉDITABLE — callback appelée avec la nouvelle valeur. */
  onSave?: (newValue: string) => Promise<void>;
  /** Render personnalisé de la valeur (lien mailto/tel, badge…). */
  renderValue?: (value: string) => ReactNode;
  /**
   * Indique l'origine de la donnée :
   *  - 'pdf'      : extrait du PDF EDOF (📄)
   *  - 'lookup'   : vient d'un autre module Zoho (🗂️)
   *  - 'manual'   : saisie manuelle pure (✍️ — pas d'icône car c'est le défaut)
   *  - 'auto'     : dérivé/inféré d'un autre champ (🔁)
   *  - 'computed' : calcul auto (🧮)
   */
  source?: 'pdf' | 'lookup' | 'manual' | 'auto' | 'computed';
  /** Si true, le champ est en lecture seule (pour les valeurs computed). */
  readOnly?: boolean;
}

/**
 * Champ éditable inline.
 *
 * Comportement :
 *  - Si `onSave` est fourni → click sur le pencil → input affiché →
 *    Enter ou bouton ✓ sauvegarde, Escape ou ✗ annule
 *  - Sinon → lecture seule (pencil non affiché)
 *
 * État de sauvegarde : loading spinner pendant l'appel, message d'erreur
 * en rouge si échec.
 */
export function EditableField({
  label,
  value,
  type = 'text',
  options,
  extra,
  highlight,
  onSave,
  renderValue,
  source,
  readOnly,
}: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<string>(value?.toString() ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);

  // Sync draft when value changes externally
  useEffect(() => {
    if (!isEditing) setDraft(value?.toString() ?? '');
  }, [value, isEditing]);

  // Focus input on edit start
  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const isEditable = !!onSave && !readOnly;
  const displayValue = value === null || value === undefined || value === '' ? '—' : String(value);

  const handleSave = async () => {
    if (!onSave) return;
    setError(null);
    setIsSaving(true);
    try {
      await onSave(draft);
      setIsEditing(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(value?.toString() ?? '');
    setIsEditing(false);
    setError(null);
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const valueClass =
    highlight === 'primary'
      ? 'inline-block rounded bg-primary-50 px-1.5 py-0.5 text-primary-700 font-semibold'
      : highlight === 'violet'
        ? 'inline-block rounded bg-violet-50 px-1.5 py-0.5 text-violet-700 font-semibold'
        : 'text-neutral-800 font-medium';

  // ─── Icône source (📄 PDF / 🗂️ lookup / 🔁 auto / 🧮 computed) ────────
  const sourceMeta = ((): { icon: ReactNode; tooltip: string; color: string } | null => {
    switch (source) {
      case 'pdf':
        return {
          icon: <FileText className="h-2.5 w-2.5" />,
          tooltip: 'Extrait du PDF EDOF importé',
          color: 'text-amber-600 bg-amber-50',
        };
      case 'lookup':
        return {
          icon: <Database className="h-2.5 w-2.5" />,
          tooltip: 'Tiré de la base Zoho (lookup)',
          color: 'text-indigo-600 bg-indigo-50',
        };
      case 'auto':
        return {
          icon: <UserIcon className="h-2.5 w-2.5" />,
          tooltip: "Dérivé d'un autre champ",
          color: 'text-violet-600 bg-violet-50',
        };
      case 'computed':
        return {
          icon: <Calculator className="h-2.5 w-2.5" />,
          tooltip: 'Calculé automatiquement',
          color: 'text-emerald-600 bg-emerald-50',
        };
      default:
        return null;
    }
  })();

  return (
    <div className="group relative flex flex-col">
      <span className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-neutral-400">
        {label}
        {sourceMeta && (
          <span
            className={`inline-flex items-center rounded-sm px-0.5 py-px ${sourceMeta.color}`}
            title={sourceMeta.tooltip}
          >
            {sourceMeta.icon}
          </span>
        )}
      </span>
      {isEditing ? (
        <div className="flex items-center gap-1">
          {options ? (
            <select
              ref={inputRef as React.RefObject<HTMLSelectElement>}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKey}
              disabled={isSaving}
              className="border-primary-400 focus:ring-primary-500 flex-1 rounded border bg-white px-1 py-0.5 text-xs focus:outline-none focus:ring-1"
            >
              <option value="">—</option>
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type={type}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKey}
              disabled={isSaving}
              className="border-primary-400 focus:ring-primary-500 flex-1 rounded border bg-white px-1 py-0.5 text-xs focus:outline-none focus:ring-1"
            />
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            title="Sauvegarder (Entrée)"
            className="rounded p-0.5 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
          >
            {isSaving ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Check className="h-3 w-3" />
            )}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            title="Annuler (Échap)"
            className="rounded p-0.5 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          {renderValue ? (
            <span className="truncate">{renderValue(displayValue)}</span>
          ) : type === 'email' && displayValue !== '—' ? (
            <a
              href={`mailto:${displayValue}`}
              className="text-primary-600 truncate hover:underline"
            >
              {displayValue}
            </a>
          ) : type === 'tel' && displayValue !== '—' ? (
            <a
              href={`tel:${displayValue.replace(/\s/g, '')}`}
              className="text-primary-600 truncate hover:underline"
            >
              {displayValue}
            </a>
          ) : (
            <span className={`truncate ${valueClass}`}>{displayValue}</span>
          )}
          {extra && <span className="text-[10px] text-neutral-400">· {extra}</span>}
          {isEditable && (
            <button
              type="button"
              title="Modifier"
              onClick={() => setIsEditing(true)}
              className="ml-auto opacity-0 transition-opacity group-hover:opacity-100"
            >
              <Pencil className="hover:text-primary-600 h-3 w-3 text-neutral-400" />
            </button>
          )}
        </div>
      )}
      {error && (
        <span className="mt-0.5 text-[9px] text-rose-600" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
